import React from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/forms/ui/card';
import type { ActivityEntry } from 'shared/src/api/activity-feed-api-types';
import { CampaignLink, LineItemLink } from './CardLinks';
import { getActivityTypeMeta, isCampaignCentricActivityType } from './activity-card-meta';
import {
  buildActivityCardAriaLabel,
  getActorDisplayName,
  isMutedActor,
  shouldLinkLineItemTitle,
  shouldRenderSubtitle
} from './activity-card-shell-utils';

type ActivityCardShellProps = {
  entry: ActivityEntry;
  showCampaignName: boolean;
  children?: React.ReactNode;
};

export function ActivityCardShell({ entry, showCampaignName, children }: ActivityCardShellProps) {
  const {
    label: typeLabel,
    colorClass,
    stripeClass,
    Icon
  } = getActivityTypeMeta(entry.activityType);
  const occurredAt = new Date(entry.occurredAt);
  const absoluteTimestamp = format(occurredAt, 'MMM d, yyyy h:mm a');
  const relativeTimestamp = formatDistanceToNow(occurredAt, { addSuffix: true });
  const actorDisplayName = getActorDisplayName(entry);
  const muteActor = isMutedActor(entry);
  const isCampaignCentric = isCampaignCentricActivityType(entry.activityType);
  const renderSubtitle = shouldRenderSubtitle(entry, showCampaignName);
  const linkLineItemTitle = shouldLinkLineItemTitle(entry);
  const ariaLabel = buildActivityCardAriaLabel({
    typeLabel,
    entityName: isCampaignCentric ? entry.campaignName : entry.entityName,
    actorDisplayName,
    absoluteTimestamp
  });

  return (
    <Card role="article" aria-label={ariaLabel} className={cn('border-l-4', stripeClass)}>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium',
                colorClass
              )}>
              <Icon className="size-3" />
              {typeLabel}
            </span>
            {entry.sourcePhase ? (
              <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                {entry.sourcePhase === 'proposal' ? 'Proposal' : 'Execution'}
              </span>
            ) : null}
          </div>
          <span className="text-muted-foreground shrink-0 text-xs">{relativeTimestamp}</span>
        </div>

        {renderTitle(entry, isCampaignCentric, linkLineItemTitle)}

        {renderSubtitle ? (
          <CampaignLink campaignId={entry.campaignId} campaignName={entry.campaignName} />
        ) : null}

        {children}

        <div className="text-muted-foreground pt-1 text-xs">
          {'by '}
          <span className={muteActor ? undefined : 'text-foreground font-medium'}>
            {actorDisplayName}
          </span>
          {' · '}
          {absoluteTimestamp}
        </div>
      </CardContent>
    </Card>
  );
}

function renderTitle(
  entry: ActivityEntry,
  isCampaignCentric: boolean,
  linkLineItemTitle: boolean
): React.ReactNode {
  if (isCampaignCentric) {
    if (!entry.campaignId || !entry.campaignName) return null;
    return (
      <CampaignLink
        campaignId={entry.campaignId}
        campaignName={entry.campaignName}
        emphasis="title"
      />
    );
  }

  if (linkLineItemTitle) {
    return (
      <LineItemLink
        campaignId={entry.campaignId}
        lineItemId={entry.lineItemId}
        lineItemName={entry.entityName}
        sourcePhase={entry.sourcePhase}
      />
    );
  }

  if (entry.entityName) {
    return <div className="text-sm font-semibold">{entry.entityName}</div>;
  }

  return null;
}
